<template lang="pug">
.reports-calendar-tab
  .mb-3
  .header.d-flex.justify-content-start.align-items-center.mb-2
    .current-date {{ _currentDate }}
    .d-flex.justify-content-end.align-items-center.flex-fill(v-if="_canCreateOriginalEvents")
      button.register-btn.btn(@click="onRegisterButton") 新しい予定
  .error-message(v-if="errorMessage")
    span {{ errorMessage }}
  CalendarOfMonth(v-model="currentDate" :events="_events" :residentGroupMap="residentGroupMap" @onEvent="onEvent")
    template(#description)
      .d-flex.flex-column.flex-fill
        .options.d-flex.flex-row.justify-content-end.align-items-center.mr-2.mb-3(v-if="_useFamilyService")
          //- フィルタリング
          span.table-option-text.mr-2 利用者グループ:
          .filter
            b-form-select(id="filter-input" v-model="filter" :options="filterOptions" size="sm")
        .d-flex.justify-content-start.align-items-center.flex-wrap
          .d-flex.justify-content-start.align-items-center(v-if="_useFamilyService")
            .resident-event-box.mr-2
            .resident-event-box-label.mr-2 利用者入所・退所
          .d-flex.justify-content-start.align-items-center(v-if="_useWaiterService")
            .inspection-reservation-event-box.mr-2
            .inspection-reservation-event-box-label.mr-2 施設見学
          .d-flex.justify-content-start.align-items-center(v-if="_useFamilyService && _isOverBasicPlan")
            .event-event-box.mr-2
            .event-event-box-label.mr-2 イベント
          .d-flex.justify-content-start.align-items-center(v-if="_useFamilyService")
            .reservation-event-box.mr-2
            .reservation-event-box-label.mr-2 面会予約
          .d-flex.justify-content-start.align-items-center(v-if="_useFamilyService && _isOverLightPlan")
            .outing-event-box.mr-2
            .outing-event-box-label.mr-2 外出外泊届け
          .d-flex.justify-content-start.align-items-center(v-if="_useOtherOfficeService")
            .online-meeting-event-box.mr-2
              .d-flex.align-items-center.justify-content-center
                img(src="@/assets/calendar-online-meeting.png")
            .online-meeting-event-box-label.mr-2 オンライン会議
  
  //- tab loading
  RelativeLoading(:isBusy="isBusy" :message="loadingMessage")
  //- page loading
  PageLoading(:isBusy="isPageBusy" :message="pageLoadingMessage")
  //- register original event modals
  CalendarInputModal(v-model="showEventInputModal" :modalTitle="eventInputModalTitle" :modelType="eventInputModalType"
    :startDatetime.sync="calendarEventStart" :startDatetimeDate.sync="calendarEventStartDate" :startDatetimeTime.sync="calendarEventStartTime"
    :endDatetime.sync="calendarEventEnd" :endDatetimeDate.sync="calendarEventEndDate" :endDatetimeTime.sync="calendarEventEndTime"
    :title.sync="calendarEventTitle" :text.sync="calendarEventText" :color.sync="calendarEventColor"
    :filterType.sync="calenderFilterType" :residentGroups.sync="calenderResidentGroups"
    @onRegister="confirmRegisterEvent" @onUpdate="confirmUpdateEvent" @onDelete="confirmDeleteEvent")
  OkCancelModal(v-model="showConfirmEventCommitModal" :title="confirmEventModaCommitMessage" @ok="onConfirmEventModalOk")
  OkModal(v-model="showSuccessEventCommitModal" :title="successEventCommitModalMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import moment, { Moment } from "moment";
import { userStore, itemListFilterStore } from "@/store/store";

import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import CalendarEventsService, {
  CalendarEventGetModel,
  CalendarEventPostModel,
  CalendarEventPatchModel,
  CalendarFilterType,
  CalendarColor
} from "@/commons/services/CalendarEvents";
import ResidentsService, { ResidentGetModel } from "@/commons/services/Residents";
import EventsService, { EventGetModel, EventHoldType, EventTargetType } from "@/commons/services/Events";
import ReservationsService, { ReservationsGetModel, ReservationType } from "@/commons/services/Reservations";
import InspectionReservationsService, { InspectionReservationsGetModel } from "@/commons/services/InspectionReservations";
import { PermittedFunctions } from "@/commons/services/Facilities";
import { PlanType } from "@/commons/services/Plans";
import OutingsService, { OutingGetModel, OutingType } from "@/commons/services/Outings";
import OtherOfficeOnlineMeetingsService, { OtherOfficeOnlineMeetingGetModel } from "@/commons/services/OtherOfficeOnlineMeetings";
import SendSystemErrorService from "@/commons/services/SendSystemError";

import RelativeLoading from "@/pc/basics/RelativeLoading.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import CalendarInputModal, { CalendarInputModalType } from "@/pc/containers/reports/CalendarInputModal.vue";
import CalendarOfMonth, { CalendarEvent, CalendarHitomeQEventType } from "@/pc/components/CalendarOfMonth.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import SystemError from "@/commons/models/SystemError";
import { WaiterServicePlanType } from "@/commons/services/WaiterServicePlans";

/**
 * ### カレンダータブコンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    RelativeLoading,
    PageLoading,
    CalendarInputModal,
    CalendarOfMonth
  }
})
export default class CalendarTab extends Vue {
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;
  /** カレンダーイベントサービス */
  @Inject("CalendarEventsService") calendarEventsService!: CalendarEventsService;
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** イベントサービス */
  @Inject("EventsService") eventsService!: EventsService;
  /** 面会予約サービス(複数) */
  @Inject("ReservationsService") reservationsService!: ReservationsService;
  /** 外出外泊サービス */
  @Inject("OutingsService") outingsService!: OutingsService;
  /** 施設見学サービス */
  @Inject("InspectionReservationsService") inspectionReservationsService!: InspectionReservationsService;
  /** 他事業所連携オンライン会議サービス */
  @Inject("OtherOfficeOnlineMeetingsService") meetingsService!: OtherOfficeOnlineMeetingsService;

  // #3782
  /** システムエラーサービス */
  @Inject("SendSystemErrorService") sendSystemErrorService!: SendSystemErrorService;

  readonly eventColors: CalendarFunctionEvent = {
    original: {
      color: "#ffffff",
      bgColor: "#646464"
    },
    resident: {
      color: "#383838",
      bgColor: "#4ad9e9"
    },
    event: {
      color: "#383838",
      bgColor: "#a0e393"
    },
    reservation: {
      color: "#383838",
      bgColor: "#edc575"
    },
    inspectionReservation: {
      color: "#383838",
      bgColor: "#c9f4f9",
      borderColor: "#19bacc"
    },
    onlineMeeting: {
      color: "#ffffff",
      bgColor: "#646464"
    },
    outing: {
      color: "#383838",
      bgColor: "#feecca"
    }
  };

  /** ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** ローディング表示可否 */
  isPageBusy: boolean = false;
  /** ローディングメッセージ */
  pageLoadingMessage: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";

  /** 現在日 */
  currentDate: Moment = moment();
  /** イベント */
  events: Array<CalendarEvent> = [];
  /** 利用者グループMAP（key：id、value：グループ情報） */
  residentGroupMap: Map<number, ResidentGroupsGetModel> | null = null;

  /** オリジナルカレンダーイベント */
  facilityCalendarEvents: Array<CalendarEventGetModel> = [];
  /** 利用者 */
  residents: Array<ResidentGetModel> = [];
  /** イベント */
  facilityEvents: Array<EventGetModel> = [];

  /** フィルタリング条件 */
  filter: string = "";
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];

  /** カレンダーイベント入力モーダル表示可否 */
  showEventInputModal: boolean = false;
  /** カレンダーイベント入力モーダルタイトル */
  eventInputModalTitle: string = "";
  /** カレンダーイベント入力種別 */
  eventInputModalType: CalendarInputModalType = CalendarInputModalType.UNKNOWN;

  /** カレンダーイベントID(画面で採番しているID) */
  eventId: number | null = null;
  /** カレンダーイベントID(DBで採番しているID) */
  calendarEventId: number | null = null;
  /** カレンダーイベント 開始日時 */
  calendarEventStart: number | null = null;
  /** カレンダーイベント 開始日時(日) */
  calendarEventStartDate: string = "";
  /** カレンダーイベント 開始日時(時) */
  calendarEventStartTime: string = "";
  /** カレンダーイベント 終了日時 */
  calendarEventEnd: number | null = null;
  /** カレンダーイベント 終了日時(日) */
  calendarEventEndDate: string = "";
  /** カレンダーイベント 終了日時(時) */
  calendarEventEndTime: string = "";
  /** カレンダーイベント タイトル */
  calendarEventTitle: string | null = null;
  /** カレンダーイベント 本文 */
  calendarEventText: string | null = null;
  /** カレンダーイベント 色 */
  calendarEventColor: number = 1;
  /** カレンダーイベント 更新日時 */
  calendarEventUpdated: number | null = null;
  /** 予定のフィルタリング用種別(0：施設全体、1：利用者グループ) */
  calenderFilterType: CalendarFilterType | null = null;
  /** 外出手段の選択の使用有無 */
  calenderResidentGroups: Array<number> | null = null;

  /** カレンダーイベント登録・更新・削除確認モーダル表示可否 */
  showConfirmEventCommitModal: boolean = false;
  /** カレンダーイベント登録・更新・削除確認モーダルメッセージ */
  confirmEventModaCommitMessage: string = "";
  /** カレンダーイベント登録・更新・削除確認モーダル種別 */
  confirmEventModaCommitType: number = -1;
  /** カレンダーイベント登録・更新・削除確認モーダル表示可否 */
  showSuccessEventCommitModal: boolean = false;
  /** カレンダーイベント登録・更新・削除確認モーダルメッセージ */
  successEventCommitModalMessage: string = "";

  errorCapture: { stack: string; message: string; actions: string[] } = { stack: "", message: "reports::CalendarTab component", actions: [] };

  /** カラーリスト */
  calendarEventColorLists: Array<CalendarColor> = [
    { background: "#646464", border: "#646464", font: "#FFFFFF" },
    { background: "#E87B7B", border: "#E87B7B", font: "#1E1E1E" },
    { background: "#F68DC2", border: "#F68DC2", font: "#1E1E1E" },
    { background: "#D098EB", border: "#D098EB", font: "#1E1E1E" },
    { background: "#7194D6", border: "#7194D6", font: "#1E1E1E" },
    { background: "#4AD9E9", border: "#4AD9E9", font: "#1E1E1E" },
    { background: "#C9F4F9", border: "#19BACC", font: "#1E1E1E" },
    { background: "#A0E393", border: "#A0E393", font: "#1E1E1E" },
    { background: "#EDC575", border: "#EDC575", font: "#1E1E1E" },
    { background: "#FEECCA", border: "#FEECCA", font: "#1E1E1E" }
  ];

  // #3782 解析用情報をAPI経由で登録する
  errorCaptured(): boolean {
    this.sendSystemErrorService.post(this.errorCapture, userStore, this.$router, this.errorCapture.actions);
    return true;
  }

  get _canCreateOriginalEvents(): boolean {
    // 家族連絡サービスのライトプラン以上
    if (this._useFamilyService && this._isOverLightPlan) {
      return true;
    }
    // 待機者サービスのベーシックプラン以上
    else if (this._useWaiterService && this._isOverWaiterBasicPlan) {
      return true;
    }
    // その他、他事業所連携使用可のみの場合等は作成不可
    return false;
  }

  get _useFamilyService(): boolean {
    return userStore.facility.useFamilyService;
  }

  /** ### プランがライト以上かを返す */
  get _isOverLightPlan(): boolean {
    const planType: PlanType = userStore.facility.planType;
    if (PlanType.LIGHT <= planType) {
      return true;
    } else {
      return false;
    }
  }

  /** ### プランがベーシック以上かを返す */
  get _isOverBasicPlan(): boolean {
    const planType: PlanType = userStore.facility.planType;
    if (PlanType.BASIC <= planType) {
      return true;
    } else {
      return false;
    }
  }

  get _useWaiterService(): boolean {
    return userStore.facility.useWaiterService && userStore.loginUser.permissions.functions.useWaiterService;
  }

  /** ### プラン(待機者)がベーシック以上かを返す */
  get _isOverWaiterBasicPlan(): boolean {
    const planType: WaiterServicePlanType = userStore.facility.waiterServicePlanType;
    if (WaiterServicePlanType.BASIC <= planType) {
      return true;
    } else {
      return false;
    }
  }

  get _useOtherOfficeService(): boolean {
    return userStore.facility.useOtherOfficeConnectionService && userStore.loginUser.permissions.functions.useOtherOfficeConnectionService;
  }

  /**
   * ### 現在日の変更監視
   *
   * - イベントを作り直す
   * - 現在日の変更時は面会予約以外の情報は画面初期表示で使用した情報を使用することで速度改善している
   */
  @Watch("currentDate")
  public async changeCurrentDate(newValue: Moment): Promise<void> {
    // #3782
    this.errorCapture.actions.push("changeCurrentDate");

    console.debug(`changeCurrentDate() year:${newValue.get("year")} month:${newValue.get("month") + 1}`);

    this.isBusy = true;
    this.loadingMessage = "読込み中...";
    this.events = await this.createCalendarEvents(this.facilityCalendarEvents, this.residents, this.facilityEvents);
    this.isBusy = false;

    return;
  }

  @Watch("calendarEventStart")
  public changeCalendarEventStart(newValue: number): void {
    // #3782
    this.errorCapture.actions.push("changeCalendarEventStart");
    // #3782
    this.errorCapture.stack = `changeCalendarEventStart() newValue:${newValue}`;

    if (newValue && this.calendarEventEnd == null) {
      const endDate: Moment = moment(this.$dateUtil.convertNumberToDate(newValue));
      endDate.add(1, "hour");
      this.calendarEventEnd = this.$dateUtil.convertDateToNumber(endDate.toDate(), "yyyymmddhh0000000");
      this.calendarEventEndDate = endDate.format("YYYY-MM-DD");
      this.calendarEventEndTime = endDate.format("HH:00:00");
    }
    return;
  }

  get _currentDate(): string {
    // #3782
    this.errorCapture.stack = `_currentDate() this.currentDate:${this.currentDate}`;

    if (this.currentDate) {
      const currentDate: Date = this.currentDate.toDate();
      return this.$dateUtil.convertDateToString(currentDate, "yyyy年mm月", false);
    } else {
      return "";
    }
  }

  get _events(): Array<CalendarEvent> {
    const filterdEvents: Array<CalendarEvent> = this.events.concat().filter((event: CalendarEvent) => {
      switch (event.hitomeQEvent.type) {
        case CalendarHitomeQEventType.MEMO: {
          const facilityEvent: CalendarEventGetModel = event.hitomeQEvent.source as CalendarEventGetModel;
          if (facilityEvent.filterType == CalendarFilterType.FACILITY) {
            return true;
          } else if (facilityEvent.filterType == CalendarFilterType.RESIDENT_GROUP) {
            if (Number(this.filter) != 0 && facilityEvent.residentGroups.indexOf(Number(this.filter)) == -1) {
              return false;
            } else {
              return true;
            }
          }
          return false;
        }
        case CalendarHitomeQEventType.EVENT_LOCAL_ONLY:
        case CalendarHitomeQEventType.EVENT_ONLINE_ONLY:
        case CalendarHitomeQEventType.EVENT_LOCAL_AND_ONLINE: {
          const facilityEvent: EventGetModel = event.hitomeQEvent.source as EventGetModel;
          if (Number(this.filter) != 0 && facilityEvent.targetType == EventTargetType.RESIDENT_GROUP) {
            let existGroup: boolean = false;
            for (const groupId of facilityEvent.targets) {
              if (groupId == Number(this.filter)) {
                existGroup = true;
                continue;
              }
            }
            return existGroup;
          }
          return true;
        }
        case CalendarHitomeQEventType.RESIDENT_IN_DATE:
        case CalendarHitomeQEventType.RESIDENT_OUT_DATE: {
          const residet: ResidentGetModel = event.hitomeQEvent.source as ResidentGetModel;
          if (Number(this.filter) != 0 && residet.groupId != Number(this.filter)) {
            return false;
          } else {
            return true;
          }
        }
        case CalendarHitomeQEventType.RESERVATION_VISIT:
        case CalendarHitomeQEventType.RESERVATION_ONLINE: {
          const reservation: ReservationsGetModel = event.hitomeQEvent.source as ReservationsGetModel;
          if (Number(this.filter) != 0 && reservation.groupId != Number(this.filter)) {
            return false;
          } else {
            return true;
          }
        }
        case CalendarHitomeQEventType.INSPECTION_RESERVATION: {
          return true;
        }
        case CalendarHitomeQEventType.ONLINE_MEETING: {
          return true;
        }
        case CalendarHitomeQEventType.OUTING_GOING_OUT:
        case CalendarHitomeQEventType.OUTING_STAYING_OUT: {
          const outing: OutingGetModel = event.hitomeQEvent.source as OutingGetModel;
          if (Number(this.filter) != 0 && outing.targetResidentInfo.groupId != Number(this.filter)) {
            return false;
          } else {
            return true;
          }
        }
        default: {
          return false;
        }
      }
    });

    return filterdEvents;
  }

  /** vueのエラーが出るので定義だけ作成している */
  set _events(newValue: Array<CalendarEvent>) {
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // #3782
    this.errorCapture.actions.push("mounted");

    await this.createView();
    return;
  }

  /**
   * ### 画面情報を作成する
   */
  public async createView(): Promise<void> {
    // #3782
    this.errorCapture.actions.push("createView");

    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const [groups, facilityCalendarEvents, residents, facilityEvents] = await Promise.all([
      this.getResidentGroups(),
      this._canCreateOriginalEvents ? this.getOriginalEvents() : Promise.resolve([]),
      this._useFamilyService ? this.getResidents() : Promise.resolve([]),
      this._useFamilyService && this._isOverBasicPlan ? this.getFacilityEvents() : Promise.resolve([])
    ]);

    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(groups);
    this.filterOptions = filterOptions;
    // フィルタの復元
    this.restoreFilterSetting();

    const residentGroupMap = this.createResidentGroupMap(groups);
    this.residentGroupMap = residentGroupMap;

    // オリジナルイベント、利用者、施設イベントは範囲指定して取得しないので最初に全て取得し、リフレッシュ以外（月移動）は再取得しない
    if (this._canCreateOriginalEvents) {
      this.facilityCalendarEvents = facilityCalendarEvents;
    }
    if (this._useFamilyService) {
      this.residents = residents;
    }
    if (this._useFamilyService && this._isOverBasicPlan) {
      this.facilityEvents = facilityEvents;
    }
    this.events = await this.createCalendarEvents(this.facilityCalendarEvents, this.residents, this.facilityEvents);

    this.isBusy = false;

    return;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - 検索用に利用者グループマスターのMAPを作る
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name];
    }
    return;
  }

  /** ### 利用者グループMAPを作成する */
  createResidentGroupMap(groups: Array<ResidentGroupsGetModel>): Map<number, ResidentGroupsGetModel> {
    const map: Map<number, ResidentGroupsGetModel> = new Map();
    for (const group of groups) {
      map.set(group.id, group);
    }
    return map;
  }

  /**
   * ### カレンダーイベントを作成する
   *
   * - 凡例通りに並べるために取得順も考慮している
   * - 以下のカレンダーイベントを作成する
   *   - オリジナル予定
   *   - 利用者入所、退所
   *   - 施設イベント
   *   - 面会予約(ただし機能使用可否を見て取得可否を判断する)
   * - 面会予約以外の情報は画面初期表示時に1回取得するだけなので、引数で渡される
   * - 面会予約の情報は範囲を設定して取得しているので、常に取得しなおしをする
   * - 作成したカレンダーイベントは昇順にソートする
   */
  public async createCalendarEvents(
    facilityCalendarEvents: Array<CalendarEventGetModel>,
    residents: Array<ResidentGetModel>,
    facilityEvents: Array<EventGetModel>
  ): Promise<Array<CalendarEvent>> {
    let events: Array<CalendarEvent> = [];
    let id: number = 1;
    console.debug("id:", id);

    const permitted: PermittedFunctions = userStore.facility.permittedFunctions;

    // 各データを並列で取得する
    const [inspectionReservations, reservations, outings, onlineMeetings] = await Promise.all([
      this._useWaiterService ? this.getInspectionReservations() : Promise.resolve([]),
      this._useFamilyService && (permitted.reservation == true || permitted.onlineReservation == true) ? this.getReservations() : Promise.resolve([]),
      this._useFamilyService && this._isOverLightPlan && permitted.outing == true ? this.getOutings() : Promise.resolve([]),
      this._useOtherOfficeService ? this.getOnlineMeetings() : Promise.resolve([])
    ]);

    // 利用者
    const residentEvents: Array<CalendarEvent> = this.createResidentEvents(residents, id);
    events = events.concat(residentEvents);
    id += residentEvents.length;
    console.debug("added residentEvents. id:", id);

    // 施設見学
    // 待機者サービス利用の場合のみ
    if (this._useWaiterService) {
      const reservationInspectionEvents: Array<CalendarEvent> = this.createInspectionReservationEvents(inspectionReservations, id);
      events = events.concat(reservationInspectionEvents);
      id += reservationInspectionEvents.length;
      console.debug("added reservationInspectionEvents. id:", id);
    }

    // 施設イベント
    const eventEvents: Array<CalendarEvent> = this.createEventEvents(facilityEvents, id);
    events = events.concat(eventEvents);
    id += eventEvents.length;
    console.debug("added eventEvents. id:", id);

    // 面会予約
    // 面会予約は家族連絡サービスを使用する場合のみイベントを取得して作成する
    if (this._useFamilyService) {
      if (permitted.reservation == true || permitted.onlineReservation == true) {
        const reservationEvents: Array<CalendarEvent> = this.createReservationEvents(reservations, id);
        events = events.concat(reservationEvents);
        id += reservationEvents.length;
        console.debug("added reservationEvents. id:", id);
      }
    }

    // 外出外泊届け
    // 外出外泊届けは家族連絡サービスを使用している かつ、ライトプラン以上のみイベントを取得して作成する
    if (this._useFamilyService && this._isOverLightPlan) {
      if (permitted.outing == true) {
        const outingEvents: Array<CalendarEvent> = this.createOutingEvents(outings, id);
        events = events.concat(outingEvents);
        id += outingEvents.length;
        console.debug("added outingEvents. id:", id);
      }
    }

    // オンライン会議
    // 他事業所連携利用の場合のみ
    if (this._useOtherOfficeService) {
      const onlineMeetingEvents: Array<CalendarEvent> = this.createOnlineMeetingEvents(onlineMeetings, id);
      events = events.concat(onlineMeetingEvents);
      id += onlineMeetingEvents.length;
      console.debug("added onlineMeetingEvents. id:", id);
    }

    // オリジナルイベント
    const calendarEvents: Array<CalendarEvent> = this.createOriginalEvents(facilityCalendarEvents, id);
    events = events.concat(calendarEvents);
    id += calendarEvents.length;
    console.debug("added originalEvents. id:", id);

    console.debug("registerd id:", id);
    const sortedEvents: Array<CalendarEvent> = this.sortCalendarEvent(events);

    return sortedEvents;
  }

  /**
   * ### カレンダーイベントを昇順ソートする
   */
  public sortCalendarEvent(events: Array<CalendarEvent>): Array<CalendarEvent> {
    // #3782
    this.errorCapture.stack = `sortCalendarEvent() events:${JSON.stringify(events)}`;

    return events.concat().sort((a, b) => {
      const aStartDate: number = this.$dateUtil.convertDateToNumber(new Date(a.start.dateTime as string), "yyyymmddhhMMss");
      const bStartDate: number = this.$dateUtil.convertDateToNumber(new Date(b.start.dateTime as string), "yyyymmddhhMMss");

      if (aStartDate > bStartDate) {
        return 1;
      }
      if (aStartDate < bStartDate) {
        return -1;
      }
      return 0;
    });
  }

  /**
   * ### カレンダーイベントを取得する
   */
  public async getOriginalEvents(): Promise<Array<CalendarEventGetModel>> {
    const response: Array<CalendarEventGetModel> = await this.calendarEventsService.gets(userStore);
    return response;
  }

  /**
   * ### カレンダー表示用のイベントを作成する
   */
  public createOriginalEvents(calendarEvents: Array<CalendarEventGetModel>, id: number): Array<CalendarEvent> {
    let events: Array<CalendarEvent> = [];

    for (const calendarEvent of calendarEvents) {
      const event: CalendarEvent = this.createOriginalEvent(calendarEvent, id);
      id++;
      events.push(event);
    }

    return events;
  }

  public createOriginalEvent(calendarEvent: CalendarEventGetModel, id: number): CalendarEvent {
    // #3782
    this.errorCapture.stack = `createOriginalEvent() id:${id}, calendarEvent.startDatetime:${calendarEvent.startDatetime}, calendarEvent.endDatetime:${calendarEvent.endDatetime}`;

    const momentFormatStartDate: Date = this.$dateUtil.convertNumberToDate(calendarEvent.startDatetime);
    const startDate: Moment = moment(momentFormatStartDate);
    const momentFormatEndDate: Date = this.$dateUtil.convertNumberToDate(calendarEvent.endDatetime);
    const endDate: Moment = moment(momentFormatEndDate);

    const event: CalendarEvent = {
      id: id,
      summary: `${calendarEvent.title}`,
      description: calendarEvent.text ? calendarEvent.text : "",
      start: {
        date: startDate.format("YYYY-MM-DD"),
        dateTime: startDate.toISOString()
      },
      end: {
        date: endDate.format("YYYY-MM-DD"),
        dateTime: endDate.toISOString()
      },
      hitomeQEvent: {
        color: calendarEvent.color.font,
        bgColor: calendarEvent.color.background,
        borderColor: calendarEvent.color.border,
        type: CalendarHitomeQEventType.MEMO,
        source: calendarEvent
      }
    };

    return event;
  }

  /**
   * ### 利用者を取得する
   *
   * - 利用者は取得制限をしないためstartIndex、endIndexを指定しない
   */
  public async getResidents(): Promise<Array<ResidentGetModel>> {
    let response: Array<ResidentGetModel> = [];

    try {
      response = await this.residentsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        const count: number = await this.residentsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount
          };
          response = response.concat(await this.residentsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### カレンダー表示用の利用者イベントを作成する
   *
   * - カレンダー用イベントを作成する
   *   - 入所日
   *   - 退所日
   */
  public createResidentEvents(residents: Array<ResidentGetModel>, id: number): Array<CalendarEvent> {
    const events: Array<CalendarEvent> = [];
    for (const resident of residents) {
      // #3782
      this.errorCapture.stack = `createResidentEvents() resident:${JSON.stringify(resident)}, inAndOutDatetimes:${JSON.stringify(resident.inAndOutDatetimes)}`;

      // v3.29.0からv3.30.0にアップデートしたタイミングでキャッシュクリアしていない施設でinAndOutDatetimesがundefinedになる可能性がある
      // undefinedの場合はスキップする
      if (!resident.inAndOutDatetimes) {
        continue;
      }
      for (const inAndOutDatetime of resident.inAndOutDatetimes) {
        if (inAndOutDatetime.inDatetime) {
          const momentFormatStartDate: Date = this.$dateUtil.convertNumberToDate(inAndOutDatetime.inDatetime);
          const startDate: Moment = moment(momentFormatStartDate);
          const endDate: Moment = startDate.clone();
          endDate.add(1, "hour");

          const event: CalendarEvent = {
            id: id,
            summary: `${resident.name}`,
            start: {
              date: startDate.format("YYYY-MM-DD"),
              dateTime: startDate.toISOString()
            },
            end: {
              date: endDate.format("YYYY-MM-DD"),
              dateTime: endDate.toISOString()
            },
            hitomeQEvent: {
              color: this.eventColors.resident.color,
              bgColor: this.eventColors.resident.bgColor,
              borderColor: this.eventColors.resident.borderColor ? this.eventColors.resident.borderColor : undefined,
              type: CalendarHitomeQEventType.RESIDENT_IN_DATE,
              source: resident
            }
          };
          id++;
          events.push(event);
        }
        if (inAndOutDatetime.outDatetime) {
          const momentFormatStartDate: Date = this.$dateUtil.convertNumberToDate(inAndOutDatetime.outDatetime);
          const startDate: Moment = moment(momentFormatStartDate);
          const endDate: Moment = startDate.clone();
          endDate.add(1, "hour");

          const event: CalendarEvent = {
            id: id,
            summary: `${resident.name}`,
            start: {
              date: startDate.format("YYYY-MM-DD"),
              dateTime: startDate.toISOString()
            },
            end: {
              date: endDate.format("YYYY-MM-DD"),
              dateTime: endDate.toISOString()
            },
            hitomeQEvent: {
              color: this.eventColors.resident.color,
              bgColor: this.eventColors.resident.bgColor,
              borderColor: this.eventColors.resident.borderColor ? this.eventColors.resident.borderColor : undefined,
              type: CalendarHitomeQEventType.RESIDENT_OUT_DATE,
              source: resident
            }
          };
          id++;
          events.push(event);
        }
      }
    }

    return events;
  }

  /**
   * ### イベントを取得する
   */
  public async getFacilityEvents(): Promise<Array<EventGetModel>> {
    let params: any = {
      needFiltering: "true"
    };
    const response: Array<EventGetModel> = await this.eventsService.gets(userStore, params);
    return response;
  }

  /**
   * ### カレンダー表示用の施設イベントを作成する
   */
  public createEventEvents(facilityEvents: Array<EventGetModel>, id: number): Array<CalendarEvent> {
    let events: Array<CalendarEvent> = [];

    for (const facilityEvent of facilityEvents) {
      // #3782
      this.errorCapture.stack = `createEventEvents() facilityEvents:${JSON.stringify(facilityEvents)}, inDatetime:${facilityEvent.timeFrom}, outDatetime:${facilityEvent.timeTo}`;

      const momentFormatStartDate: Date = this.$dateUtil.convertNumberToDate(facilityEvent.timeFrom);
      const startDate: Moment = moment(momentFormatStartDate);
      const momentFormatEndDate: Date = this.$dateUtil.convertNumberToDate(facilityEvent.timeTo);
      const endDate: Moment = moment(momentFormatEndDate);

      let type: CalendarHitomeQEventType = CalendarHitomeQEventType.UNKNOWN;
      switch (facilityEvent.holdType) {
        case EventHoldType.LOCAL_ONLY: {
          type = CalendarHitomeQEventType.EVENT_LOCAL_ONLY;
          break;
        }
        case EventHoldType.ONLINE_ONLY: {
          type = CalendarHitomeQEventType.EVENT_ONLINE_ONLY;
          break;
        }
        case EventHoldType.LOCAL_AND_ONLINE: {
          type = CalendarHitomeQEventType.EVENT_LOCAL_AND_ONLINE;
          break;
        }
        default: {
          type = CalendarHitomeQEventType.UNKNOWN;
          break;
        }
      }

      const event: CalendarEvent = {
        id: id,
        summary: `${facilityEvent.title}`,
        start: {
          date: startDate.format("YYYY-MM-DD"),
          dateTime: startDate.toISOString()
        },
        end: {
          date: endDate.format("YYYY-MM-DD"),
          dateTime: endDate.toISOString()
        },
        hitomeQEvent: {
          color: this.eventColors.event.color,
          bgColor: this.eventColors.event.bgColor,
          borderColor: this.eventColors.event.borderColor ? this.eventColors.event.borderColor : undefined,
          type: type,
          source: facilityEvent
        }
      };
      id++;
      events.push(event);
    }

    return events;
  }

  /**
   * ### 面会予約を取得する
   */
  public async getReservations(): Promise<Array<ReservationsGetModel>> {
    // #3782
    this.errorCapture.stack = `getReservations() currentDate:${this.currentDate}`;

    // 範囲日時を指定する
    let currentDate: Moment = this.currentDate.clone();
    const start: number = this.$dateUtil.convertDateToNumber(currentDate.toDate(), "yyyymm01000000000");
    currentDate.endOf("month");
    const end: number = this.$dateUtil.convertDateToNumber(currentDate.toDate(), "yyyymmdd235900000");
    let params: any = {
      start: start,
      end: end
    };
    const response: Array<ReservationsGetModel> = await this.reservationsService.gets(userStore, params);
    return response;
  }

  /**
   * ### カレンダー表示用の面会予約イベントを作成する
   */
  public createReservationEvents(reservations: Array<ReservationsGetModel>, id: number): Array<CalendarEvent> {
    let events: Array<CalendarEvent> = [];

    for (const reservation of reservations) {
      // #3782
      this.errorCapture.stack = `createReservationEvents() reservations:${JSON.stringify(reservations)}, reservation:${JSON.stringify(reservation)}, datetime:${
        reservation.datetime
      }`;

      const momentFormatStartDate: Date = this.$dateUtil.convertNumberToDate(reservation.datetime as number);
      const startDate: Moment = moment(momentFormatStartDate);
      const endDate: Moment = startDate.clone();
      endDate.add(1, "hour");

      const event: CalendarEvent = {
        id: id,
        summary: `${reservation.residentName}`,
        start: {
          date: startDate.format("YYYY-MM-DD"),
          dateTime: startDate.toISOString()
        },
        end: {
          date: endDate.format("YYYY-MM-DD"),
          dateTime: endDate.toISOString()
        },
        hitomeQEvent: {
          color: this.eventColors.reservation.color,
          bgColor: this.eventColors.reservation.bgColor,
          borderColor: this.eventColors.reservation.borderColor ? this.eventColors.reservation.borderColor : undefined,
          type: reservation.type == ReservationType.ONLINE ? CalendarHitomeQEventType.RESERVATION_ONLINE : CalendarHitomeQEventType.RESERVATION_VISIT,
          source: reservation
        }
      };
      id++;
      events.push(event);
    }

    return events;
  }

  /**
   * ### 外出外泊届けを取得する
   */
  public async getOutings(): Promise<Array<OutingGetModel>> {
    // #3782
    this.errorCapture.stack = `getOutings() currentDate:${this.currentDate}`;

    // 範囲日時を指定する
    let currentDate: Moment = this.currentDate.clone();
    const start: number = this.$dateUtil.convertDateToNumber(currentDate.toDate(), "yyyymm01000000000");
    currentDate.endOf("month");
    const end: number = this.$dateUtil.convertDateToNumber(currentDate.toDate(), "yyyymmdd235900000");
    let params: any = {
      start: start,
      end: end
    };
    const response: Array<OutingGetModel> = await this.outingsService.gets(userStore, params);
    return response;
  }

  /**
   * ### カレンダー表示用の外出外泊届けイベントを作成する
   */
  public createOutingEvents(outings: Array<OutingGetModel>, id: number): Array<CalendarEvent> {
    let events: Array<CalendarEvent> = [];

    for (const outing of outings) {
      // 削除済みも取得されるので、削除済みはスキップする
      if (outing.deleteFlag) {
        continue;
      }

      // #3782
      this.errorCapture.stack = `createOutingEvents() outings:${JSON.stringify(outings)}, outing:${JSON.stringify(outing)}, startDatetime:${outing.startDatetime}, endDatetime:${
        outing.endDatetime
      }`;

      const momentFormatStartDate: Date = this.$dateUtil.convertNumberToDate(outing.startDatetime as number);
      const startDate: Moment = moment(momentFormatStartDate);
      const momentFormatSEndDate: Date = this.$dateUtil.convertNumberToDate(outing.endDatetime as number);
      const endDate: Moment = moment(momentFormatSEndDate);

      const event: CalendarEvent = {
        id: id,
        summary: `${outing.targetResidentInfo.name}`,
        start: {
          date: startDate.format("YYYY-MM-DD"),
          dateTime: startDate.toISOString()
        },
        end: {
          date: endDate.format("YYYY-MM-DD"),
          dateTime: endDate.toISOString()
        },
        hitomeQEvent: {
          color: this.eventColors.outing.color,
          bgColor: this.eventColors.outing.bgColor,
          borderColor: this.eventColors.outing.borderColor ? this.eventColors.outing.borderColor : undefined,
          type: outing.type == OutingType.GOING_OUT ? CalendarHitomeQEventType.OUTING_GOING_OUT : CalendarHitomeQEventType.OUTING_STAYING_OUT,
          source: outing
        }
      };
      id++;
      events.push(event);
    }

    return events;
  }

  /**
   * ### 施設見学予約を取得する
   */
  public async getInspectionReservations(): Promise<Array<InspectionReservationsGetModel>> {
    // #3782
    this.errorCapture.stack = `getInspectionReservations() currentDate:${this.currentDate}`;

    // 範囲日時を指定する
    let currentDate: Moment = this.currentDate.clone();
    const start: number = this.$dateUtil.convertDateToNumber(currentDate.toDate(), "yyyymm01000000000");
    currentDate.endOf("month");
    const end: number = this.$dateUtil.convertDateToNumber(currentDate.toDate(), "yyyymmdd235900000");
    let params: any = {
      start: start,
      end: end
    };
    const response: Array<InspectionReservationsGetModel> = await this.inspectionReservationsService.gets(userStore, params);
    return response;
  }

  /**
   * ### カレンダー表示用の施設見学予約イベントを作成する
   */
  public createInspectionReservationEvents(inspectionReservations: Array<InspectionReservationsGetModel>, id: number): Array<CalendarEvent> {
    let events: Array<CalendarEvent> = [];

    for (const reservation of inspectionReservations) {
      if (reservation.approved == false || !reservation.datetime) {
        continue;
      }

      // #3782
      this.errorCapture.stack = `createInspectionReservationEvents() inspectionReservations:${JSON.stringify(inspectionReservations)}, reservation:${JSON.stringify(
        reservation
      )}, datetime:${reservation.datetime}`;

      const momentFormatStartDate: Date = this.$dateUtil.convertNumberToDate(reservation.datetime as number);
      const startDate: Moment = moment(momentFormatStartDate);
      const endDate: Moment = startDate.clone();
      endDate.add(1, "hour");

      const event: CalendarEvent = {
        id: id,
        summary: `${reservation.name}`,
        start: {
          date: startDate.format("YYYY-MM-DD"),
          dateTime: startDate.toISOString()
        },
        end: {
          date: endDate.format("YYYY-MM-DD"),
          dateTime: endDate.toISOString()
        },
        hitomeQEvent: {
          color: this.eventColors.inspectionReservation.color,
          bgColor: this.eventColors.inspectionReservation.bgColor,
          borderColor: this.eventColors.inspectionReservation.borderColor ? this.eventColors.inspectionReservation.borderColor : undefined,
          type: CalendarHitomeQEventType.INSPECTION_RESERVATION,
          source: reservation
        }
      };
      id++;
      events.push(event);
    }

    return events;
  }

  /**
   * ### オンライン会議を取得する
   */
  public async getOnlineMeetings(): Promise<Array<OtherOfficeOnlineMeetingGetModel>> {
    const currentDate: Moment = this.currentDate.clone();
    const start: number = this.$dateUtil.convertDateToNumber(currentDate.toDate(), "yyyymm01000000000");
    currentDate.endOf("month");
    const end: number = this.$dateUtil.convertDateToNumber(currentDate.toDate(), "yyyymmdd235900000");
    let params: any = {
      start: start,
      end: end
    };
    const response: Array<OtherOfficeOnlineMeetingGetModel> = await this.meetingsService.gets(userStore, params);
    return response;
  }

  /**
   * ### カレンダー表示用のオンライン会議イベントを作成する
   */
  public createOnlineMeetingEvents(onlineMeetings: Array<OtherOfficeOnlineMeetingGetModel>, id: number): Array<CalendarEvent> {
    let events: Array<CalendarEvent> = [];

    for (const onlineMeeting of onlineMeetings) {
      // #3782
      this.errorCapture.stack = `createOnlineMeetingEvents() onlineMeetings:${JSON.stringify(onlineMeetings)}, reservation:${JSON.stringify(onlineMeeting)}, datetime:${
        onlineMeeting.startDatetime
      }`;

      const momentFormatStartDate: Date = this.$dateUtil.convertNumberToDate(onlineMeeting.startDatetime as number);
      const startDate: Moment = moment(momentFormatStartDate);
      const momentFormatEndDate: Date = this.$dateUtil.convertNumberToDate(onlineMeeting.endDatetime as number);
      const endDate: Moment = moment(momentFormatEndDate);

      const event: CalendarEvent = {
        id: id,
        summary: `${onlineMeeting.title}`,
        start: {
          date: startDate.format("YYYY-MM-DD"),
          dateTime: startDate.toISOString()
        },
        end: {
          date: endDate.format("YYYY-MM-DD"),
          dateTime: endDate.toISOString()
        },
        hitomeQEvent: {
          color: this.eventColors.onlineMeeting.color,
          bgColor: this.eventColors.onlineMeeting.bgColor,
          borderColor: this.eventColors.onlineMeeting.borderColor ? this.eventColors.onlineMeeting.borderColor : undefined,
          type: CalendarHitomeQEventType.ONLINE_MEETING,
          source: onlineMeeting
        }
      };
      id++;
      events.push(event);
    }

    return events;
  }

  /**
   * ### カレンダーイベント押下イベント
   */
  public onEvent(event: CalendarEvent): void {
    // #3782
    this.errorCapture.actions.push("onEvent");

    if (event.hitomeQEvent.type == CalendarHitomeQEventType.MEMO) {
      this.onOriginalEventEdit(event);
    }
    return;
  }

  /**
   * ### カレンダー入力モーダルに渡す情報を初期化する
   */
  public initCalendarEventInput(): void {
    this.eventId = null;
    this.calendarEventId = null;
    this.calendarEventStart = null;
    this.calendarEventStartDate = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    this.calendarEventStartTime = "10:00:00";
    this.calendarEventEnd = null;
    this.calendarEventEndDate = "";
    this.calendarEventEndTime = "";
    this.calendarEventTitle = null;
    this.calendarEventText = null;
    this.calendarEventColor = 1;
    this.calendarEventUpdated = null;
    this.calenderFilterType = null;
    this.calenderResidentGroups = null;
    return;
  }

  /**
   * ### 確認モーダルOKボタン押下処理
   *
   * - 事前に設定された種別に応じて、登録・更新・削除処理を行う
   */
  public async onConfirmEventModalOk(): Promise<void> {
    // #3782
    this.errorCapture.actions.push("onConfirmEventModalOk");

    if (this.confirmEventModaCommitType == 0) {
      await this.registerEvent();
    } else if (this.confirmEventModaCommitType == 1) {
      await this.updateEvent();
    } else if (this.confirmEventModaCommitType == 2) {
      await this.deleteEvent();
    }
  }

  /**
   * ### カレンダーイベント新規登録ボタン押下イベント
   */
  public onRegisterButton(): void {
    this.initCalendarEventInput();

    this.eventInputModalTitle = "オリジナル予定の登録";
    this.eventInputModalType = CalendarInputModalType.REGISTER;
    this.showEventInputModal = true;
    return;
  }

  /**
   * ### カレンダーイベント登録確認モーダルを表示する
   */
  public confirmRegisterEvent(): void {
    // #3782
    this.errorCapture.actions.push("confirmRegisterEvent");

    this.confirmEventModaCommitMessage = "予定を登録しますか？";
    this.showConfirmEventCommitModal = true;
    this.confirmEventModaCommitType = 0;
    return;
  }

  /**
   * ### カレンダーイベントを登録する
   */
  public async registerEvent(): Promise<void> {
    this.isPageBusy = true;
    this.pageLoadingMessage = "登録中...";

    const calendarRegisterRequestBody: CalendarEventPostModel = this.createCalendarRegisterRequestBody();
    const registerdEvent: CalendarEventGetModel = await this.calendarEventsService.post(userStore, calendarRegisterRequestBody);

    // カレンダーイベントをitemに追加するのみで再リクエストはしない
    let id: number = this.events.length + 1;
    const event: CalendarEvent = this.createOriginalEvent(registerdEvent, id);
    let newEvents: Array<CalendarEvent> = this.events.concat();
    newEvents.push(event);
    this.events = this.sortCalendarEvent(newEvents);

    this.successEventCommitModalMessage = "登録に成功しました。";
    this.showSuccessEventCommitModal = true;
    this.showEventInputModal = false;
    this.isPageBusy = false;

    return;
  }

  /**
   * ### カレンダーイベント登録用のリクエストボディを作成する
   */
  public createCalendarRegisterRequestBody(): CalendarEventPostModel {
    const requestBody: CalendarEventPostModel = {
      startDatetime: this.calendarEventStart as number,
      endDatetime: this.calendarEventEnd as number,
      title: this.calendarEventTitle as string,
      text: this.calendarEventText ? this.calendarEventText : null,
      color: this.calendarEventColorLists[this.calendarEventColor - 1],
      filterType: this.calenderFilterType as CalendarFilterType,
      residentGroups: this.calenderResidentGroups ? this.calenderResidentGroups : []
    };
    return requestBody;
  }

  /**
   * ### カレンダーイベント押下(編集・削除)イベント
   */
  public onOriginalEventEdit(event: CalendarEvent): void {
    // #3782
    this.errorCapture.stack = `onOriginalEventEdit() event:${JSON.stringify(event)}, event.hitomeQEvent.source:${JSON.stringify(event.hitomeQEvent.source)}`;

    this.initCalendarEventInput();

    const calendarEvent: CalendarEventGetModel = event.hitomeQEvent.source;
    this.eventId = event.id;
    this.calendarEventId = calendarEvent.id;
    this.calendarEventStart = calendarEvent.startDatetime;
    this.calendarEventStartDate = this.$dateUtil.convertNumberToString(calendarEvent.startDatetime, "yyyy-mm-dd");
    this.calendarEventStartTime = this.$dateUtil.convertNumberToString(calendarEvent.startDatetime, "hh:MM:ss");
    this.calendarEventEnd = calendarEvent.endDatetime;
    this.calendarEventEndDate = this.$dateUtil.convertNumberToString(calendarEvent.endDatetime, "yyyy-mm-dd");
    this.calendarEventEndTime = this.$dateUtil.convertNumberToString(calendarEvent.endDatetime, "hh:MM:ss");
    this.calendarEventTitle = calendarEvent.title;
    this.calendarEventText = calendarEvent.text;
    this.calendarEventColor = this.getCalendarEventColorNumber(calendarEvent);
    this.calendarEventUpdated = calendarEvent.updated;
    this.calenderFilterType = calendarEvent.filterType;
    this.calenderResidentGroups = calendarEvent.residentGroups;

    this.eventInputModalTitle = "オリジナル予定の編集";
    this.eventInputModalType = CalendarInputModalType.EDIT;
    this.showEventInputModal = true;
    return;
  }

  private getCalendarEventColorNumber(calendarEvent: CalendarEventGetModel): number {
    for (let i = 0; i < this.calendarEventColorLists.length; i++) {
      if (this.calendarEventColorLists[i].background == calendarEvent.color.background) {
        return i + 1;
      }
    }
    return 0;
  }

  /**
   * ### カレンダーイベント更新確認モーダルを表示する
   */
  public confirmUpdateEvent(): void {
    // #3782
    this.errorCapture.actions.push("confirmUpdateEvent");

    this.confirmEventModaCommitMessage = "予定を更新しますか？";
    this.showConfirmEventCommitModal = true;
    this.confirmEventModaCommitType = 1;
    return;
  }

  /**
   * ### カレンダーイベントを更新する
   */
  public async updateEvent(): Promise<void> {
    this.isPageBusy = true;
    this.pageLoadingMessage = "更新中...";

    try {
      const calendarUpdateRequestBody: CalendarEventPatchModel = this.createCalendarUpdateRequestBody();
      const result: CalendarEventGetModel = await this.calendarEventsService.patch(userStore, this.calendarEventId as number, calendarUpdateRequestBody);

      let newEvents: Array<CalendarEvent> = this.events.concat();
      const updateTargetEventId: number = this.eventId as number;
      for (const event of newEvents) {
        if (event.id == updateTargetEventId) {
          // #3782
          this.errorCapture.stack = `updateEvent() newEvents:${JSON.stringify(newEvents)}, event:${JSON.stringify(
            event
          )}, calendarUpdateRequestBody:${calendarUpdateRequestBody}, startDatetime:${calendarUpdateRequestBody.startDatetime}, endDatetime:${
            calendarUpdateRequestBody.endDatetime
          }`;

          event.summary = calendarUpdateRequestBody.title;
          event.description = calendarUpdateRequestBody.text ? calendarUpdateRequestBody.text : undefined;

          const momentFormatStartDate: Date = this.$dateUtil.convertNumberToDate(calendarUpdateRequestBody.startDatetime);
          const startDate: Moment = moment(momentFormatStartDate);
          event.start = {
            date: startDate.format("YYYY-MM-DD"),
            dateTime: startDate.toISOString()
          };

          const momentFormatEndDate: Date = this.$dateUtil.convertNumberToDate(calendarUpdateRequestBody.endDatetime);
          const endDate: Moment = moment(momentFormatEndDate);
          event.end = {
            date: endDate.format("YYYY-MM-DD"),
            dateTime: endDate.toISOString()
          };

          event.hitomeQEvent.bgColor = calendarUpdateRequestBody.color.background;
          event.hitomeQEvent.borderColor = calendarUpdateRequestBody.color.border;
          event.hitomeQEvent.color = calendarUpdateRequestBody.color.font;

          // 更新結果のレコード情報で更新する
          event.hitomeQEvent.source = result;

          this.$forceUpdate(); // 強制再描画をしないとカレンダーの背景色と文字色が更新されない
        }
      }
      this.events = newEvents;

      this.successEventCommitModalMessage = "更新に成功しました。";
      this.showSuccessEventCommitModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.showEventInputModal = false;
      this.isPageBusy = false;
    }

    return;
  }

  /**
   * ### カレンダーイベント登録用のリクエストボディを作成する
   */
  public createCalendarUpdateRequestBody(): CalendarEventPatchModel {
    const requestBody: CalendarEventPatchModel = {
      startDatetime: this.calendarEventStart as number,
      endDatetime: this.calendarEventEnd as number,
      title: this.calendarEventTitle as string,
      text: this.calendarEventText ? this.calendarEventText : null,
      color: this.calendarEventColorLists[this.calendarEventColor - 1],
      filterType: this.calenderFilterType as CalendarFilterType,
      residentGroups: this.calenderResidentGroups ? this.calenderResidentGroups : [],
      updated: this.calendarEventUpdated as number
    };
    return requestBody;
  }

  /**
   * ### カレンダーイベント削除確認モーダルを表示する
   */
  public confirmDeleteEvent(): void {
    // #3782
    this.errorCapture.actions.push("confirmDeleteEvent");

    this.confirmEventModaCommitMessage = "予定を削除しますか？";
    this.showConfirmEventCommitModal = true;
    this.confirmEventModaCommitType = 2;
    return;
  }

  /**
   * ### カレンダーイベントを削除する
   */
  public async deleteEvent(): Promise<void> {
    this.isPageBusy = true;
    this.pageLoadingMessage = "削除中...";

    await this.calendarEventsService.delete(userStore, this.calendarEventId as number);

    // 削除したイベントをeventsから削除するだけで再リクエストはしない
    const updateTargetEventId: number = this.eventId as number;
    this.events = this.events.concat().filter((event: CalendarEvent) => {
      if (event.id == updateTargetEventId) {
        return false;
      } else {
        return true;
      }
    });

    this.successEventCommitModalMessage = "削除に成功しました。";
    this.showSuccessEventCommitModal = true;
    this.showEventInputModal = false;
    this.isPageBusy = false;

    return;
  }

  /**
   * ### タブ表示を最新の情報に更新する
   */
  public async refresh(): Promise<void> {
    await this.createView();
    return;
  }

  setFilterSetting() {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name, filter: this.filter });
    }
    return;
  }
}

interface CalendarFunctionEvent {
  original: CalendarEventColor;
  resident: CalendarEventColor;
  event: CalendarEventColor;
  reservation: CalendarEventColor;
  inspectionReservation: CalendarEventColor;
  onlineMeeting: CalendarEventColor;
  outing: CalendarEventColor;
}

interface CalendarEventColor {
  color: string;
  bgColor: string;
  borderColor?: string;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.reports-calendar-tab {
  position: relative;

  .current-date {
    font-weight: bold;
    font-size: 22px;
  }

  .register-btn {
    width: 120px;
  }

  .options {
    .filter {
      min-width: 320px;
      max-width: 420px;
    }
  }

  .resident-event-box,
  .event-event-box,
  .reservation-event-box,
  .inspection-reservation-event-box,
  .online-meeting-event-box,
  .outing-event-box,
  .original-event-box {
    height: 24px;
    width: 40px;
    border-radius: 4px;
  }
  .resident-event-box-label,
  .event-event-box-label,
  .reservation-event-box-label,
  .inspection-reservation-event-box-label,
  .online-meeting-event-box-label,
  .outing-event-box-label,
  .original-event-box-label {
    font-size: 12px;
  }
  .resident-event-box {
    background-color: #4ad9e9;
    border: solid 2px #4ad9e9;
  }
  .event-event-box {
    background-color: #a0e393;
    border: solid 2px #a0e393;
  }
  .reservation-event-box {
    background-color: #edc575;
    border: solid 2px #edc575;
  }
  .inspection-reservation-event-box {
    background-color: #c9f4f9;
    border: solid 2px #19bacc;
  }
  .online-meeting-event-box {
    background-color: #646464;
    border: solid 2px #646464;
  }
  .outing-event-box {
    background-color: #feecca;
    border: solid 2px #feecca;
  }
}
</style>
